import { LeftOutlined } from '@ant-design/icons'
import { Button, InputNumber, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import FoodSafetyDayNav from 'src/components/food-safety/FoodSafetyDayNav'
import {
  FoodSafetyExceptionFollowUp,
  FoodSafetyExceptionStatus,
  NeedsActionBadge,
} from 'src/components/food-safety/FoodSafetyExceptionFollowUp'
import FoodSafetyUnresolvedStrip from 'src/components/food-safety/FoodSafetyUnresolvedStrip'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySubmitErrorBox,
  FoodSafetySubmitErrorLead,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import useFoodSafetyExceptionFollowUps from 'src/hooks/useFoodSafetyExceptionFollowUps'
import useFoodSafetyLinkedCheck from 'src/hooks/useFoodSafetyLinkedCheck'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import useFoodSafetySubmissionKeys from 'src/hooks/useFoodSafetySubmissionKeys'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import useFoodSafetyNewDayReload from 'src/hooks/useFoodSafetyNewDayReload'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import {
  createFoodSafetyLog,
  fetchFoodSafetyDay,
  recordFoodSafetyExceptionRecheck,
} from 'src/utils/foodSafetyApi'
import { trackFoodSafetyLogSubmitted } from 'src/utils/foodSafetyAnalytics'
import {
  FOOD_SAFETY_NEEDS_ACTION_PARAM,
  countFoodSafetyOpenExceptions,
  formatFoodSafetyNeedsActionBadge,
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  isFoodSafetyRecheckLog,
  resolveFoodSafetyRecheckValue,
  validateFoodSafetyCorrectiveDraft,
  FOOD_SAFETY_CORRECTIVE_FIELD,
  focusFoodSafetyCorrectiveField,
} from 'src/utils/foodSafetyCorrectiveAction'
import {
  FOOD_SAFETY_SUBMIT_ERROR_LEAD,
  formatFoodSafetySubmitError,
  resolveFoodSafetySubmitAffordance,
} from 'src/utils/foodSafetyFeedback'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import { formatFridgeDueBy } from 'src/utils/foodSafetyFridges'
import {
  buildFoodSafetyBrowseWindow,
  isFoodSafetyViewingToday,
  resolveFoodSafetyScheduleBadge,
  foodSafetyHomeHref,
  formatFoodSafetyOverviewBackLabel,
} from 'src/utils/foodSafetyHome'
import {
  buildProbeLogBody,
  evaluateProbeReadings,
  formatProbeFailHeadline,
  formatProbeFailedTestsHeadline,
  formatProbeLogLine,
  formatProbeTestSummary,
  isDishwasherUnit,
  probeInputNumberValue,
  isProbeBothTests,
  isProbeLatestTestPassing,
  listProbeLogFollowUps,
  probePanelReading,
} from 'src/utils/foodSafetyProbe'
import {
  setupAddButtonLabel,
  setupEmptyStateCopy,
} from 'src/utils/foodSafetySetup'
import {
  foodSafetyTemperatureBoundError,
  probeDraftHasImpossibleTemperature,
} from 'src/utils/foodSafetyTemperatureBounds'
import {
  FOOD_SAFETY_TEMP_TONE_COLOUR,
  foodSafetyTempToneFromOutOfRange,
} from 'src/utils/foodSafetyTempColour'

const emptyDraft = () => ({
  value: null,
  boilingValue: null,
  action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
  actionNudged: false,
})

const focusFirstActionChip = id =>
  focusFoodSafetyCorrectiveField(
    `probe-corrective-${id}`,
    FOOD_SAFETY_CORRECTIVE_FIELD.ACTIONS,
  )

const FoodSafetyProbeCalibrationScreen = ({
  section: SECTION = FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
}) => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const history = useHistory()
  const location = useLocation()
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = String(venue?._id || routeVenueId || '')

  const queryDate = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('date') || undefined
  }, [location.search])
  const jumpToNeedsAction = useMemo(
    () =>
      new URLSearchParams(location.search).get(
        FOOD_SAFETY_NEEDS_ACTION_PARAM,
      ) === '1',
    [location.search],
  )

  const [day, setDay] = useState(null)
  const newDayKey = useFoodSafetyNewDayReload(day)
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState({})
  const [actionErrors, setActionErrors] = useState({})
  const [submitErrors, setSubmitErrors] = useState({})
  const [submittingId, setSubmittingId] = useState(null)
  const [testingAgain, setTestingAgain] = useState({})
  const [historyPaywallOpen, setHistoryPaywallOpen] = useState(false)
  const [lockAccess, setLockAccess] = useState(null)
  const latestDay = useFoodSafetyLatestRequest()
  const saveLock = useFoodSafetySaveLock()
  const submissionKeys = useFoodSafetySubmissionKeys()

  const loadDay = useCallback(async () => {
    if (!venueId) return
    const isCurrent = latestDay.begin()
    setLoading(true)
    try {
      const data = await fetchFoodSafetyDay(venueId, {
        section: SECTION,
        date: queryDate,
      })
      if (!isCurrent()) return
      setDay(data)
    } catch (error) {
      if (!isCurrent()) return
      if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        setLockAccess(error.historyAccess || null)
        setHistoryPaywallOpen(true)
        message.warning(error.message)
        const earliest = error.historyAccess?.window?.earliestLocalDate
        if (earliest && earliest !== queryDate) {
          history.replace(
            `/v/${venueId}/food-safety/section/${SECTION}?date=${earliest}`,
          )
        }
        return
      }
      console.error(error)
      message.error(
        error?.message ||
          `Could not load ${
            SECTION === FOOD_SAFETY_SECTION.DISHWASHERS ? 'dishwasher' : 'probe'
          } checks.`,
      )
    } finally {
      isCurrent.done()
      if (isCurrent()) setLoading(false)
    }
  }, [venueId, queryDate, history, latestDay, SECTION])

  useEffect(() => {
    loadDay()
  }, [loadDay, newDayKey])

  /** Reload in place; resolves to the new day, or null when a switch won. */
  const refreshDay = useCallback(async () => {
    if (!venueId || !day?.localDate) return null
    const isCurrent = latestDay.beginQuiet()
    if (isCurrent.skipped) return null
    const data = await fetchFoodSafetyDay(venueId, {
      section: SECTION,
      date: day.localDate,
    })
    if (!isCurrent()) return null
    setDay(data)
    return data
  }, [venueId, day?.localDate, latestDay, SECTION])

  const followUps = useFoodSafetyExceptionFollowUps({
    venueId,
    onSaved: refreshDay,
  })
  const resetFollowUps = followUps.reset

  useEffect(() => {
    setDrafts({})
    setActionErrors({})
    setSubmitErrors({})
    setTestingAgain({})
    resetFollowUps()
  }, [queryDate, newDayKey, resetFollowUps])

  const browseWindow = useMemo(
    () =>
      buildFoodSafetyBrowseWindow(
        day?.historyAccess || lockAccess,
        day?.localDate,
      ),
    [day?.historyAccess, day?.localDate, lockAccess],
  )

  const goToSectionDate = nextDate => {
    if (!venueId || !nextDate) return
    history.push(
      `/v/${venueId}/food-safety/section/${SECTION}?date=${nextDate}`,
    )
  }

  const clearFor = id => prev => {
    if (!prev[id]) return prev
    const next = { ...prev }
    delete next[id]
    return next
  }

  const patchDraft = (id, patch) => {
    setSubmitErrors(clearFor(id))
    setDrafts(prev => ({
      ...prev,
      [id]: { ...(prev[id] || emptyDraft()), ...patch },
    }))
  }

  const patchAction = (id, patch) => {
    setActionErrors(clearFor(id))
    setDrafts(prev => {
      const draft = prev[id] || emptyDraft()
      return {
        ...prev,
        [id]: { ...draft, action: { ...draft.action, ...patch } },
      }
    })
  }

  const viewingToday = isFoodSafetyViewingToday(
    day?.localDate,
    day?.todayLocalDate,
  )

  const submit = async item => {
    const def = item.definition
    const id = String(def._id)
    const draft = drafts[id] || emptyDraft()
    const dishwasher = isDishwasherUnit(def.config)
    const both = isProbeBothTests(def.config) || dishwasher
    const blank = v => v == null || v === ''
    if (probeDraftHasImpossibleTemperature(draft)) return
    if (blank(draft.value) || (both && blank(draft.boilingValue))) {
      message.warning(
        dishwasher
          ? 'Enter both readings — the wash and the rinse.'
          : both
          ? 'Enter both readings — ice water and boiling water.'
          : 'Enter what the probe reads.',
      )
      return
    }
    const { failedTests } = evaluateProbeReadings(draft, def.config)
    const failing = failedTests.length > 0
    const actionError = failing
      ? validateFoodSafetyCorrectiveDraft(draft.action)
      : null
    if (actionError) {
      setActionErrors(prev => ({ ...prev, [id]: actionError }))
      return
    }
    const recheckValue =
      failing && viewingToday && failedTests.length === 1
        ? resolveFoodSafetyRecheckValue(draft.action)
        : null

    if (!saveLock.tryAcquire(id)) return
    setSubmittingId(id)
    const saveSlot = `${day.localDate}:${id}`
    try {
      let saved
      try {
        saved = await createFoodSafetyLog(
          venueId,
          buildProbeLogBody({
            definitionId: id,
            localDate: day.localDate,
            value: draft.value,
            boilingValue: both ? draft.boilingValue : null,
            action: failing ? draft.action : null,
          }),
          {
            submissionKey: submissionKeys.keyFor(saveSlot, {
              value: draft.value,
              boilingValue: both ? draft.boilingValue : null,
              action: failing ? draft.action : null,
            }),
          },
        )
      } catch (err) {
        const msg = formatFoodSafetySubmitError(err)
        setSubmitErrors(prev => ({ ...prev, [id]: msg }))
        message.error(msg)
        return
      }
      submissionKeys.clear(saveSlot)
      trackFoodSafetyLogSubmitted({ venueId, section: SECTION })
      const passed = saved?.payload?.calibrationPassed ?? !failing
      if (passed) {
        message.success(`${def.name} passed`)
      } else if (failing && draft.action?.type) {
        message.success(`${def.name} failed — action recorded`)
      } else {
        message.warning(`${def.name} failed — needs corrective action`)
      }
      if (recheckValue != null && saved?.exception?._id) {
        try {
          await recordFoodSafetyExceptionRecheck(venueId, saved.exception._id, {
            value: recheckValue,
          })
        } catch (recheckError) {
          message.warning(
            `Check saved, but the recheck didn’t save — ${formatFoodSafetySubmitError(
              recheckError,
            )}`,
          )
        }
      }
      setDrafts(clearFor(id))
      setActionErrors(clearFor(id))
      setTestingAgain(clearFor(id))
      if (!(await refreshAfterFoodSafetySave(refreshDay))) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      }
    } finally {
      saveLock.release(id)
      setSubmittingId(null)
    }
  }

  /** First Enter on a failing reading shows the actions; the next saves. */
  const submitFromInput = item => {
    const id = String(item.definition._id)
    if (saveLock.isHeld(id)) return
    const draft = drafts[id] || emptyDraft()
    if (probeDraftHasImpossibleTemperature(draft)) return
    const failing =
      evaluateProbeReadings(draft, item.definition.config).passed === false
    if (failing && !draft.action?.type && !draft.actionNudged) {
      patchDraft(id, { actionNudged: true })
      focusFirstActionChip(id)
      return
    }
    submit(item)
  }

  useFoodSafetyLinkedCheck({
    search: location.search,
    items: day?.items,
    cardTestId: id => `probe-card-${id}`,
    itemsReady: !loading && Boolean(day),
    venueId,
    history,
  })

  if (blocked) return <PermissionBlockScreen />
  if (!venueId || (loading && !day)) return <Loading />

  const items = day?.items || []
  const overviewHref = foodSafetyHomeHref(venueId, {
    localDate: day?.localDate,
    todayLocalDate: day?.todayLocalDate,
  })
  const overviewLabel = formatFoodSafetyOverviewBackLabel(
    day?.localDate,
    day?.todayLocalDate,
  )

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={overviewHref} data-testid='fs-section-back'>
          <LeftOutlined /> {overviewLabel}
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>
              {FOOD_SAFETY_SECTION_LABELS[SECTION]}
            </FoodSafetyPageTitle>
            <BodyText>
              {SECTION === FOOD_SAFETY_SECTION.DISHWASHERS
                ? 'Record the wash and final-rinse temperatures — we check them against the limits configured for this dishwasher.'
                : 'Put the probe in ice water, boiling water or both and record what it reads — we work out pass or fail.'}
            </BodyText>
          </div>
          {day?.localDate ? (
            <FoodSafetyDayNav
              localDate={day.localDate}
              browseWindow={browseWindow}
              onChangeDate={goToSectionDate}
              onBrowseLocked={() => setHistoryPaywallOpen(true)}
              disabled={loading}
            />
          ) : null}
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      {day?.localDate ? (
        <FoodSafetyUnresolvedStrip
          venueId={venueId}
          section={SECTION}
          viewedLocalDate={day.localDate}
          todayLocalDate={day.todayLocalDate}
          refreshKey={day}
          onResolved={refreshDay}
          autoJump={jumpToNeedsAction}
          testIdPrefix='probe-unresolved'
        />
      ) : null}

      {items.length === 0 ? (
        <EmptyCard data-testid='probe-empty'>
          <H3>{setupEmptyStateCopy(SECTION).title}</H3>
          <BodyText>
            {canManage
              ? setupEmptyStateCopy(SECTION).body
              : `Ask a manager to add ${
                  SECTION === FOOD_SAFETY_SECTION.DISHWASHERS
                    ? 'dishwashers'
                    : 'temperature probes'
                } in Food Safety setup.`}
          </BodyText>
          {canManage ? (
            <SetupLink
              to={`/v/${venueId}/food-safety/setup?section=${SECTION}&add=1`}
            >
              {setupAddButtonLabel(SECTION)}
            </SetupLink>
          ) : null}
        </EmptyCard>
      ) : (
        <CardList>
          {items.map(item => {
            const def = item.definition
            const id = String(def._id)
            const config = def.config || {}
            const draft = drafts[id] || emptyDraft()
            const dishwasher = isDishwasherUnit(config)
            const both = isProbeBothTests(config) || dishwasher
            const iceBoundError = foodSafetyTemperatureBoundError(draft.value)
            const boilingBoundError = foodSafetyTemperatureBoundError(
              draft.boilingValue,
            )
            const evaluation = evaluateProbeReadings(draft, config)
            const iceResult = iceBoundError ? null : evaluation.ice
            const boilingResult = boilingBoundError ? null : evaluation.boiling
            const passing =
              iceBoundError || boilingBoundError ? null : evaluation.passed
            const failing = passing === false
            const failedTests = evaluation.failedTests
            const toneFor = result =>
              result == null
                ? 'unknown'
                : foodSafetyTempToneFromOutOfRange(!result)
            const actionChosen = draft.action?.type || ''
            const affordance = resolveFoodSafetySubmitAffordance({
              outOfRange: failing,
              corrective: actionChosen,
              actionOptional: true,
              saveFailed: Boolean(submitErrors[id]),
            })
            const logs = (item.logs || []).filter(
              log => !isFoodSafetyRecheckLog(log),
            )
            const scheduleBadge = resolveFoodSafetyScheduleBadge(item)
            const needsActionBadge = formatFoodSafetyNeedsActionBadge(
              countFoodSafetyOpenExceptions(logs),
            )
            const dueLabel = item.completed
              ? null
              : formatFridgeDueBy(def.dueByTime)
            const settled = isProbeLatestTestPassing(logs)
            const showEntry = !settled || Boolean(testingAgain[id])
            const inputTone =
              passing == null
                ? 'unknown'
                : foodSafetyTempToneFromOutOfRange(failing)

            return (
              <ProbeCard key={id} data-testid={`probe-card-${id}`}>
                <CardHeader>
                  <div>
                    <ProbeName>{def.name}</ProbeName>
                    <MetaLine data-testid={`probe-meta-${id}`}>
                      {[
                        config.probeReference,
                        formatProbeTestSummary(config),
                        dueLabel,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </MetaLine>
                  </div>
                  {needsActionBadge ? (
                    <NeedsActionBadge
                      data-testid={`probe-needs-action-badge-${id}`}
                    >
                      {needsActionBadge}
                    </NeedsActionBadge>
                  ) : scheduleBadge?.tone === 'missed' ? (
                    <MissedBadge>{scheduleBadge.label}</MissedBadge>
                  ) : scheduleBadge ? (
                    <QuietBadge>{scheduleBadge.label}</QuietBadge>
                  ) : null}
                </CardHeader>

                {logs.length > 0 ? (
                  <LogList>
                    {logs.map(log => {
                      const line = formatProbeLogLine(log, config)
                      const followUpRows = listProbeLogFollowUps(log, config)
                      const perTest = followUpRows.length > 1
                      return (
                        <LogRow
                          key={String(log._id)}
                          data-testid={`probe-log-${log._id}`}
                        >
                          <LogResult>
                            <LogTemp data-tone={line.tone}>
                              {line.tempLabel}
                            </LogTemp>
                            {line.statusLabel ? (
                              <LogTemp data-tone={line.tone}>
                                {' · '}
                                {line.statusLabel}
                              </LogTemp>
                            ) : null}
                            {line.expectedLabel ? (
                              <LogExpected>
                                {' — '}
                                {line.expectedLabel}
                              </LogExpected>
                            ) : null}
                          </LogResult>
                          {perTest ? null : (
                            <FoodSafetyExceptionStatus
                              exception={followUpRows[0].exception}
                              testId={`probe-needs-action-${log._id}`}
                            />
                          )}
                          <LogMeta>{line.meta}</LogMeta>
                          {followUpRows.map(row => {
                            const followUp = (
                              <FoodSafetyExceptionFollowUp
                                key={row.key}
                                exception={row.exception}
                                section={SECTION}
                                fallbackReading={row.fallbackReading}
                                viewingToday={viewingToday}
                                followUps={followUps}
                                testIdPrefix='probe'
                                rowKey={row.key}
                              />
                            )
                            if (!perTest) return followUp
                            return (
                              <TestProblem
                                key={row.key}
                                data-testid={`probe-test-problem-${row.key}`}
                              >
                                <TestProblemLabel>{row.label}</TestProblemLabel>
                                <FoodSafetyExceptionStatus
                                  exception={row.exception}
                                  testId={`probe-needs-action-${row.key}`}
                                />
                                {followUp}
                              </TestProblem>
                            )
                          })}
                        </LogRow>
                      )
                    })}
                  </LogList>
                ) : null}

                {!showEntry ? (
                  <TestAgainLink
                    type='button'
                    onClick={() => {
                      setTestingAgain(prev => ({ ...prev, [id]: true }))
                      requestAnimationFrame(() =>
                        document.getElementById(`probe-reading-${id}`)?.focus(),
                      )
                    }}
                    data-testid={`probe-test-again-${id}`}
                  >
                    Log another test
                  </TestAgainLink>
                ) : null}

                {showEntry ? (
                  <EntryRow>
                    <EntryField
                      data-tone={both ? toneFor(iceResult) : inputTone}
                    >
                      <EntryLabel htmlFor={`probe-reading-${id}`}>
                        {dishwasher
                          ? 'Wash temp'
                          : both
                          ? logs.length && !settled
                            ? 'Check again — ice water reads'
                            : 'Ice water reads'
                          : logs.length && !settled
                          ? 'Check again — probe reads'
                          : 'Probe reads'}
                      </EntryLabel>
                      <EntryInputRow>
                        <InputNumber
                          id={`probe-reading-${id}`}
                          value={probeInputNumberValue(draft.value)}
                          onChange={value =>
                            patchDraft(id, {
                              value: probeInputNumberValue(value),
                              actionNudged: false,
                            })
                          }
                          onPressEnter={() => submitFromInput(item)}
                          placeholder='°C'
                          step={0.1}
                          style={{ width: '7rem' }}
                          data-testid={`probe-reading-${id}`}
                        />
                        <span>°C</span>
                        {both && iceResult != null ? (
                          <LiveResult
                            data-tone={toneFor(iceResult)}
                            data-testid={`probe-live-result-ice-${id}`}
                          >
                            {iceResult ? 'Pass' : 'Fail'}
                          </LiveResult>
                        ) : null}
                        {!both && passing != null ? (
                          <LiveResult
                            data-tone={inputTone}
                            data-testid={`probe-live-result-${id}`}
                          >
                            {passing ? 'Pass' : 'Fail'}
                          </LiveResult>
                        ) : null}
                      </EntryInputRow>
                      {iceBoundError ? (
                        <BoundNote data-testid={`probe-bound-${id}`}>
                          {iceBoundError}
                        </BoundNote>
                      ) : null}
                    </EntryField>
                    {both ? (
                      <EntryField data-tone={toneFor(boilingResult)}>
                        <EntryLabel htmlFor={`probe-boiling-reading-${id}`}>
                          {dishwasher
                            ? 'Final rinse temp'
                            : 'Boiling water reads'}
                        </EntryLabel>
                        <EntryInputRow>
                          <InputNumber
                            id={`probe-boiling-reading-${id}`}
                            value={probeInputNumberValue(draft.boilingValue)}
                            onChange={boilingValue =>
                              patchDraft(id, {
                                boilingValue:
                                  probeInputNumberValue(boilingValue),
                                actionNudged: false,
                              })
                            }
                            onPressEnter={() => submitFromInput(item)}
                            placeholder='°C'
                            step={0.1}
                            style={{ width: '7rem' }}
                            data-testid={`probe-boiling-reading-${id}`}
                          />
                          <span>°C</span>
                          {boilingResult != null ? (
                            <LiveResult
                              data-tone={toneFor(boilingResult)}
                              data-testid={`probe-live-result-boiling-${id}`}
                            >
                              {boilingResult ? 'Pass' : 'Fail'}
                            </LiveResult>
                          ) : null}
                        </EntryInputRow>
                        {boilingBoundError ? (
                          <BoundNote data-testid={`probe-bound-boiling-${id}`}>
                            {boilingBoundError}
                          </BoundNote>
                        ) : null}
                      </EntryField>
                    ) : null}
                    <Button
                      type={affordance.quiet ? 'default' : 'primary'}
                      data-chrome={affordance.quiet ? 'quiet' : undefined}
                      loading={submittingId === id}
                      onClick={() => {
                        if (affordance.needsChoice) {
                          patchDraft(id, { actionNudged: true })
                          focusFirstActionChip(id)
                          return
                        }
                        submit(item)
                      }}
                      data-testid={`probe-submit-${id}`}
                    >
                      {affordance.label}
                    </Button>
                  </EntryRow>
                ) : null}

                {failing ? (
                  <FoodSafetyCorrectiveActionPanel
                    section={SECTION}
                    headline={
                      both
                        ? formatProbeFailedTestsHeadline(failedTests)
                        : formatProbeFailHeadline(draft.value, config)
                    }
                    reading={
                      both
                        ? probePanelReading(
                            failedTests[0].value,
                            failedTests[0],
                          )
                        : probePanelReading(draft.value, config)
                    }
                    draft={draft.action}
                    onChange={patch => patchAction(id, patch)}
                    showRecheck={viewingToday && failedTests.length === 1}
                    error={actionErrors[id]}
                    focusOnError
                    onNoteEnter={() => submit(item)}
                    onSaveWithoutAction={() => submit(item)}
                    testIdPrefix={`probe-corrective-${id}`}
                  />
                ) : null}

                {submitErrors[id] ? (
                  <FoodSafetySubmitErrorBox
                    data-testid={`probe-submit-error-${id}`}
                    role='status'
                  >
                    <FoodSafetySubmitErrorLead>
                      {FOOD_SAFETY_SUBMIT_ERROR_LEAD}
                    </FoodSafetySubmitErrorLead>
                    {submitErrors[id]}
                  </FoodSafetySubmitErrorBox>
                ) : null}
              </ProbeCard>
            )
          })}
        </CardList>
      )}

      <FoodSafetyReportsPaywallModal
        visible={historyPaywallOpen}
        reason={FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER}
        olderRecordCount={(day?.historyAccess || lockAccess)?.olderRecordCount}
        upgradeHint={(day?.historyAccess || lockAccess)?.upgradeHint}
        onClose={() => setHistoryPaywallOpen(false)}
      />
    </Wrapper>
  )
}

const TestAgainLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: underline;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  cursor: pointer;
`

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  ${ollieFabPageClearanceCss}

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    ${ollieFabPageClearanceCss}
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const EmptyCard = styled(FoodSafetySurfaceCard)`
  border-style: dashed;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const SetupLink = styled(Link)`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  align-self: flex-start;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ProbeCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-start;
`

const ProbeName = styled(H3)`
  margin: 0;
  font-size: 1.05rem;
`

const MetaLine = styled.div`
  margin-top: 0.15rem;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const QuietBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
`

const MissedBadge = styled(QuietBadge)`
  color: #c45c26;
`

const LogList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const LogRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`

const LogResult = styled.div`
  font-size: 0.95rem;
`

const LogTemp = styled.span`
  font-weight: 700;
  color: ${({ 'data-tone': tone }) =>
    FOOD_SAFETY_TEMP_TONE_COLOUR[tone] || 'inherit'};
`

const LogExpected = styled.span`
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const LogMeta = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const TestProblem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.5rem 0 0 0.6rem;
  border-left: 2px solid #f0d9b5;
`

const TestProblemLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
`

const EntryRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const EntryField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const EntryLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const EntryInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

const BoundNote = styled.p`
  margin: 0;
  max-width: 16rem;
  font-size: 0.8rem;
  line-height: 1.35;
  color: #a8071a;
`

const LiveResult = styled.span`
  font-weight: 700;
  color: ${({ 'data-tone': tone }) =>
    FOOD_SAFETY_TEMP_TONE_COLOUR[tone] || 'inherit'};
`

export default FoodSafetyProbeCalibrationScreen
